import { useEffect, useRef, useState } from "react";
import { Eye, Users2, BarChart3, AlertOctagon, Workflow, Gauge } from "lucide-react";

const COMPONENTS = [
  { key: "vision", icon: Eye, name: "Visión", text: "Todos viendo lo mismo." },
  { key: "personas", icon: Users2, name: "Personas", text: "Las personas correctas en los puestos correctos." },
  { key: "datos", icon: BarChart3, name: "Datos", text: "Métricas que muestran el pulso del negocio." },
  { key: "asuntos", icon: AlertOctagon, name: "Asuntos", text: "Identificar, discutir y resolver de raíz." },
  { key: "procesos", icon: Workflow, name: "Procesos", text: "La forma correcta de hacer las cosas." },
  { key: "traccion", icon: Gauge, name: "Tracción", text: "Disciplina y accountability semana a semana." },
];

// Order clockwise starting from top (VISIÓN), matching the official EOS wheel.
// `step` defines animation entry order requested by Carlos.
const WHEEL = [
  { key: "vision",   label: "VISIÓN",   angle: 270, fill: "#F26522", step: 1 },
  { key: "datos",    label: "DATOS",    angle: 330, fill: "#F47A35", step: 3 },
  { key: "procesos", label: "PROCESOS", angle: 30,  fill: "#E55A1A", step: 5 },
  { key: "traccion", label: "TRACCIÓN", angle: 90,  fill: "#F26522", step: 6 },
  { key: "asuntos",  label: "ASUNTOS",  angle: 150, fill: "#E55A1A", step: 4 },
  { key: "personas", label: "PERSONAS", angle: 210, fill: "#F47A35", step: 2 },
];

const STEP_DELAY_MS = 180;
const CENTER_DELAY_MS = 0;

export default function SixComponents() {
  return (
    <section id="eos-en-chile" data-testid="six-components-section" className="section-padding bg-slate-50/60">
      <div className="container-eos">
        <div className="max-w-3xl reveal">
          <span className="eyebrow">El modelo EOS</span>
          <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy-800 leading-tight">
            Los 6 componentes que fortalecen tu empresa.
          </h2>
          <p className="mt-5 text-lg text-slate-600">
            EOS ayuda a ordenar lo esencial para que la empresa funcione con más
            claridad, foco y ejecución.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Wheel */}
          <div className="lg:col-span-6 reveal">
            <PieWheel />
          </div>

          {/* List */}
          <div className="lg:col-span-6">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {COMPONENTS.map((c, i) => (
                <li
                  key={c.key}
                  data-testid={`component-${c.key}`}
                  className="reveal group flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-[#F26522]/40 hover:shadow-md transition-all"
                  style={{ transitionDelay: `${i * 60}ms` }}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#F26522]/10 flex items-center justify-center shrink-0 transition-colors group-hover:bg-[#F26522]">
                    <c.icon className="w-5 h-5 text-[#F26522] group-hover:text-white transition-colors" strokeWidth={1.7} />
                  </div>
                  <div>
                    <p className="font-display font-bold text-navy-800">{c.name}</p>
                    <p className="text-sm text-slate-600 mt-0.5 leading-snug">{c.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function PieWheel() {
  const CX = 300;
  const CY = 300;
  const R_OUTER = 285;
  const R_CENTER = 78;
  const R_LABEL = 195;
  const GAP_DEG = 2;

  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (!("IntersectionObserver" in window)) {
      setStarted(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setStarted(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const polar = (r, deg) => {
    const rad = (deg * Math.PI) / 180;
    return [CX + r * Math.cos(rad), CY + r * Math.sin(rad)];
  };

  const wedgePath = (centerAngle) => {
    const a1 = centerAngle - 30 + GAP_DEG;
    const a2 = centerAngle + 30 - GAP_DEG;
    const [x1, y1] = polar(R_OUTER, a1);
    const [x2, y2] = polar(R_OUTER, a2);
    return `M ${CX} ${CY} L ${x1} ${y1} A ${R_OUTER} ${R_OUTER} 0 0 1 ${x2} ${y2} Z`;
  };

  // Common animation style helper
  const enter = (delayMs) => ({
    transformOrigin: `${CX}px ${CY}px`,
    transformBox: "view-box",
    opacity: started ? 1 : 0,
    transform: started ? "scale(1)" : "scale(0.86)",
    transition: `opacity 700ms cubic-bezier(0.16,1,0.3,1) ${delayMs}ms, transform 700ms cubic-bezier(0.16,1,0.3,1) ${delayMs}ms`,
    willChange: "opacity, transform",
  });

  return (
    <div ref={ref} className="relative mx-auto w-full max-w-[520px] aspect-square">
      <svg
        viewBox="0 0 600 600"
        className="absolute inset-0 w-full h-full drop-shadow-[0_20px_40px_rgba(242,101,34,0.25)]"
        role="img"
        aria-label="Los 6 componentes de EOS: Visión, Personas, Datos, Asuntos, Procesos y Tracción"
      >
        {/* Wedges (each animates in based on its `step`) */}
        {WHEEL.map((w) => {
          const [tx, ty] = polar(R_LABEL, w.angle);
          const delay = CENTER_DELAY_MS + 320 + (w.step - 1) * STEP_DELAY_MS;
          return (
            <g key={w.key} data-testid={`wheel-wedge-${w.key}`} style={enter(delay)}>
              <path d={wedgePath(w.angle)} fill={w.fill} />
              <text
                x={tx}
                y={ty}
                fill="#FFFFFF"
                fontFamily="'Plus Jakarta Sans', system-ui, sans-serif"
                fontWeight="800"
                fontSize="28"
                letterSpacing="1.2"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{ paintOrder: "stroke" }}
              >
                {w.label}
              </text>
            </g>
          );
        })}

        {/* Center white circle — enters FIRST */}
        <g data-testid="wheel-center" style={enter(CENTER_DELAY_MS)}>
          <circle cx={CX} cy={CY} r={R_CENTER + 6} fill="#FFFFFF" />
          <circle
            cx={CX}
            cy={CY}
            r={R_CENTER}
            fill="#FFFFFF"
            stroke="#F26522"
            strokeWidth="2"
            opacity="0.95"
          />
          <text
            x={CX}
            y={CY - 8}
            fill="#0A1128"
            fontFamily="'Plus Jakarta Sans', system-ui, sans-serif"
            fontWeight="800"
            fontSize="22"
            textAnchor="middle"
            dominantBaseline="middle"
            letterSpacing="0.5"
          >
            TU
          </text>
          <text
            x={CX}
            y={CY + 18}
            fill="#F26522"
            fontFamily="'Plus Jakarta Sans', system-ui, sans-serif"
            fontWeight="800"
            fontSize="22"
            textAnchor="middle"
            dominantBaseline="middle"
            letterSpacing="0.5"
          >
            EMPRESA
          </text>
        </g>
      </svg>
    </div>
  );
}
